import { useTranslation } from "react-i18next"
import { Member } from "../patents"
import { isImported, maintenanceActionsStatus, maintenanceActionStatusToString, renewalStatusExtraction, renewalStatusToString } from "src/renewal/dennemeyer/state/modern_states"
import { calculateNextDay, eliminateDoubleMaintenanceActions, useAugmentMaintenanceAction } from "src/renewal/dennemeyer/utils"
import { PageResponse, useDennemeyer, useDmSettings, useFullIpRightsPortfolio, useMaintenances, useTimeline } from "src/renewal/dennemeyer/DennemeyerProvider"
import { useFilter } from "src/filter/Filter"
import _ from "lodash"
import { InfiniteData } from "@tanstack/react-query"
import { AnnuityFieldsContext } from "./AnnuityFields"

function progressCount<T>(data: InfiniteData<PageResponse<T>>) {
    let count = {current: 0, total: 0}
    if (data?.pages?.length > 0) {
        const lastPage = data.pages[data.pages.length - 1].Data
        if (lastPage) {
            const pageSize = lastPage.PageSize
            const currentPage = lastPage.CurrentPageNumber
            const isLastPage = lastPage.IsLastPage
            const total = lastPage.TotalRows
            if (isLastPage) {
                count = {current: total, total}
            } else {
                count = {current: pageSize * currentPage, total}
            }
        }
    }
    return count
}

export function DmAnnuityFieldsProvider({children}) {
  const {t} = useTranslation()

  console.log("USING DM PRvoder")

    const { settings } = useDmSettings()
    const startDate = settings?.startDate
    const { ipRightByDennemeyerId, } = useDennemeyer()
    const { calculateDueDates } = useTimeline()
    const {showActiveOnly} = useFilter()
    const { augmentMaintenanceAction, augmentIpRight } = useAugmentMaintenanceAction(!showActiveOnly)

    const { instructionByDennemeyerId, ipRightByMemberId: _pcIpRightByMemberId, upIpRightByMemberId, costCentersByIpRightId, validationsByIpRightId } = useDennemeyer()
    const pcIpRightByMemberId = {..._pcIpRightByMemberId, ...upIpRightByMemberId}

    const {data: maintenanceActions, fetchNextPage, hasNextPage, isFetching: isFetchingMaintenances} = useMaintenances({onlyInstructable: false, onlyOpen: false})
    if (hasNextPage) {
        fetchNextPage()
    }

    const { data: ipRights, fetchNextPage: fetchNextIpRightsPage, hasNextPage: hasNextIpRightsPage, isFetching: isFetchingPortfolio } = useFullIpRightsPortfolio()
    if (hasNextIpRightsPage) {
        //console.log('fetching next page')
        fetchNextIpRightsPage()
    }

    //console.log({hasNextIpRightsPage, hasNextPage})
    //const isLoadingAnnuity = isLoadingMaintenances || isLoadingPortfolio
    const isLoadingAnnuity = isFetchingMaintenances || isFetchingPortfolio
    //console.log({isLoadingAnnuity, l: maintenanceActions?.pages?.flatMap(p => p?.Data?.Page ?? []).length, r: ipRights?.pages?.flatMap(p => p?.Data?.Page ?? []).length})

    const ipRightByMemberId = _(ipRights?.pages?.flatMap(p => p?.Data?.Page ?? []))
        .filter(ipr => ipr.DennemeyerId in ipRightByDennemeyerId)
        .map(augmentIpRight)
        .filter(ipr => ipr?.member?.familyMemberId !== undefined)
        .keyBy(ipr => ipr.member.familyMemberId)
        .value()
    // console.log({augmentedIpRightByMemberId})

    const maintenanceActionByMemberId = _(maintenanceActions?.pages?.flatMap(p => p?.Data?.Page ?? []))
        .map(augmentMaintenanceAction)
        .filter(m => m.member?.familyMemberId !== undefined && (startDate === undefined || m.instructionDueDate >= startDate))
        .groupBy(m => m.member?.familyMemberId)
        .mapValues(ms => _(eliminateDoubleMaintenanceActions(ms)).values().maxBy(m => m.instructionDueDate))
        .value()
    // console.log({maintenanceActionByMemberId})

    const ipRightsCount = progressCount(ipRights)
    const maintenanceActionsCount = progressCount(maintenanceActions)

    const finalProgressCount = {current: ipRightsCount.current + maintenanceActionsCount.current, total: ipRightsCount.total + maintenanceActionsCount.total}
    const progress = parseInt(finalProgressCount.current / finalProgressCount.total * 100 + '') - (isLoadingAnnuity ? 1 : 0)
    const loadingString = `Loading (${isNaN(progress) ? 0 : progress}%) ...`
  // TODO
  const annuityContext = {
    'renewals-status': (
      member: Member /*{isLoadingAnnuity, loadingString, pcIpRightByMemberId, ipRightByMemberId, validationsByIpRightId}: AnnuityDataCalculationContext*/,
    ) => {
      // TODO: do loading check in render cell
      if (isLoadingAnnuity) return loadingString
      const pcIpRight = pcIpRightByMemberId[member.familyMemberId]
      const ipRight = ipRightByMemberId[member.familyMemberId]
      const status = renewalStatusExtraction({ member, pcIpRight, ipRight, validationErrors: validationsByIpRightId[pcIpRight?.ipRightId] })
      return renewalStatusToString(status, t)
    },
    dm_validation_errors: (m: Member /*{pcIpRightByMemberId, validationsByIpRightId}: AnnuityDataCalculationContext*/) => {
      const pcIpRight = pcIpRightByMemberId[m.familyMemberId]
      const messages = validationsByIpRightId[pcIpRight?.ipRightId] ?? []
      return messages.map(m => m.message).join('\n')
    },
    'last-instruction': (
      member: Member,
      /*{ isLoadingAnnuity, loadingString, pcIpRightByMemberId, ipRightByMemberId, validationsByIpRightId, calculateDueDates, instructionByDennemeyerId, maintenanceActionByMemberId }: AnnuityDataCalculationContext*/
    ) => {
      if (isLoadingAnnuity) return loadingString
      const pcIpRight = pcIpRightByMemberId[member.familyMemberId]
      const ipRight = ipRightByMemberId[member.familyMemberId]
      const renewalStatus = renewalStatusExtraction({
        member,
        pcIpRight,
        ipRight,
        validationErrors: validationsByIpRightId[pcIpRight?.ipRightId],
      })
      // console.log(member.internalReference, {pcIpRight, ipRight, renewalStatus})
      if (isImported(renewalStatus)) {
        const maintenanceActions = [maintenanceActionByMemberId[member.familyMemberId]].filter(Boolean)
        const maintenanceActionHistory = maintenanceActionsStatus({
          renewalStatus: renewalStatus.status,
          maintenanceActions,
          calculateDueDates,
          instructionByDennemeyerId,
        })
        // console.log({maintenanceActionHistory, maintenanceActions})
        // if (renewalStatus.status !== 'not-handled') {
        return maintenanceActionStatusToString(maintenanceActionHistory, t)
        // }
      }
      return ''
    },
    'fee-due': (m: Member /*{maintenanceActionByMemberId}: AnnuityDataCalculationContext*/) => {
      return maintenanceActionByMemberId[m.familyMemberId]?.DueDate ?? ''
    },
    'instruction-due': (
      m: Member /*{ calculateDueDates, instructionByDennemeyerId, maintenanceActionByMemberId }: AnnuityDataCalculationContext*/,
    ) => {
      const action = calculateNextDay(
        [maintenanceActionByMemberId[m.familyMemberId]].filter(Boolean),
        calculateDueDates,
        instructionByDennemeyerId,
      )
      return action?.openAction?.instructionDueDate ?? ''
    },
    fees: (m: Member /*{maintenanceActionByMemberId}: AnnuityDataCalculationContext*/) => {
      return maintenanceActionByMemberId[m.familyMemberId]?.fees ?? {}
    },
    'cost-centers': (m: Member /*{costCentersByIpRightId, pcIpRightByMemberId}: AnnuityDataCalculationContext*/) =>
      (costCentersByIpRightId[pcIpRightByMemberId[m.familyMemberId]?.ipRightId] ?? [])
        .map(cc => (cc.percentage === 100 ? cc.name : `${cc.name} (${cc.percentage}%)`))
        .join(', '),
  }
  return (
    <AnnuityFieldsContext.Provider value={{ annuityFields: annuityContext, isLoading: isLoadingAnnuity }}>
      {children}
    </AnnuityFieldsContext.Provider>
  )
}